import { formatCurrency } from './currency';

export const ACTIVITY_FILTERS = [['all', 'All'], ['income', 'Money Added'], ['spending', 'Spending'], ['chores', 'Chores'], ['savings', 'Savings']];

export const matchesActivityType = (activity, type) => {
  if (type === 'income') return activity.activity_kind === 'transaction' && activity.type === 'income';
  if (type === 'spending') return activity.activity_kind === 'transaction' && activity.type === 'expense';
  if (type === 'chores') return activity.activity_kind === 'transaction' && activity.type === 'income' && activity.category === 'Chores';
  if (type === 'savings') return activity.activity_kind === 'savings';
  return true;
};

// Calculate before filtering/paging: savings and pending/rejected transactions
// do not change the child's Piggy Bank Total.
export const getPiggyAmountsByActivityId = (allActivity) => {
    const balancesByChild = new Map();
    const amountsByActivity = new Map();

    const chronologicalActivity = [...allActivity].sort((a, b) => {
      const aTime = new Date(a.created_at || 0).getTime();
      const bTime = new Date(b.created_at || 0).getTime();

      if (aTime !== bTime) return aTime - bTime;

      return String(a.activity_id || '').localeCompare(String(b.activity_id || ''));
    });

    chronologicalActivity.forEach((activity) => {
      const childId = String(activity.child_id || '');
      let balanceCents = balancesByChild.get(childId) || 0;

      if (
        activity.activity_kind === 'transaction' &&
        activity.approval_status === 'approved'
      ) {
        const amountCents = Number(activity.amount_cents) || 0;

        if (activity.type === 'income') {
          balanceCents += amountCents;
        } else if (activity.type === 'expense') {
          balanceCents -= amountCents;
        }

        balancesByChild.set(childId, balanceCents);
      }

      amountsByActivity.set(activity.activity_id, balanceCents);
    });

    return amountsByActivity;

};

export const getParentActivityDisplay = (activity, childNameMap) => {
    const childName = childNameMap.get(String(activity.child_id)) || 'Kid';

    if (activity.activity_kind === 'savings') {
      const isDeposit = activity.savings_activity_type === 'deposit';
      const isTransfer = activity.savings_activity_type === 'transfer';
      const isReturn =
        activity.savings_activity_type === 'cancel' ||
        activity.savings_activity_type === 'withdrawal';

      return {
        childName,
        title: activity.display_title || 'Savings Activity',
        category: isTransfer ? 'Savings Transfer' : isDeposit ? 'Savings' : 'Savings Return',
        amountText: `${isReturn ? '+' : ''}${formatCurrency(activity.amount_cents || 0)}`,
        amountClass: isReturn
          ? 'text-emerald-600 dark:text-emerald-400'
          : isTransfer
          ? 'text-blue-600 dark:text-blue-400'
          : 'text-purple-700 dark:text-purple-300',
        status: '',
      };
    }

    const isIncome = activity.type === 'income';
    const isPending = activity.approval_status === 'pending';
    const isRejected = activity.approval_status === 'rejected';
    const isChore = activity.category === 'Chores';

    const title =
      activity.note ||
      (isChore ? 'Chore Earnings' : '') ||
      activity.category ||
      activity.subtype ||
      (isIncome ? 'Money Added' : 'Money Spent');

    const category = isChore
      ? 'Chore'
      : isIncome
      ? String(activity.subtype || 'Income').replace(/^./, (letter) => letter.toUpperCase())
      : 'Spending';

    return {
      childName,
      title,
      category,
      amountText: `${isIncome ? '+' : '-'}${formatCurrency(activity.amount_cents || 0)}`,
      amountClass:
        isPending || isRejected
          ? 'text-gray-500 dark:text-slate-400'
          : isIncome
          ? 'text-emerald-600 dark:text-emerald-400'
          : 'text-red-600 dark:text-red-400',
      status: isPending ? 'Pending' : isRejected ? 'Rejected' : '',
    };
  };

export const formatActivityDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const csvCell = (value, userText = false) => {
  let text = String(value ?? '');
  // Keep user-entered names/notes from becoming spreadsheet formulas.
  if (userText && /^[=+\-@\t\r\n]/.test(text.trimStart())) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
};

export const makeActivityCsv = ({ activities, childFilter, childNameMap, piggyAmounts, includeDetails = false }) => {
  const headers = ['Date', ...(childFilter === 'all' ? ['Child'] : []), 'Activity', 'Category', 'Amount', 'Piggy Bank', ...(includeDetails ? ['Timestamp', 'Status', 'Note', 'Subtype'] : [])];
  const rows = activities.map((activity) => {
    const display = getParentActivityDisplay(activity, childNameMap);
    const cells = [csvCell(formatActivityDate(activity.created_at)), ...(childFilter === 'all' ? [csvCell(display.childName, true)] : []), csvCell(display.status ? `${display.title} (${display.status})` : display.title, true), csvCell(display.category, true), csvCell(display.amountText), csvCell(formatCurrency(piggyAmounts.get(activity.activity_id) || 0))];
    if (includeDetails) cells.push(csvCell(activity.created_at), csvCell(activity.approval_status || ''), csvCell(activity.note || activity.display_subtitle || '', true), csvCell(activity.subtype || '', true));
    return cells.join(',');
  });
  return [headers.map((header) => csvCell(header)).join(','), ...rows].join('\r\n');
};

export const downloadCsv = (csv, filename) => {
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};
